/**
 * Dimensions Step Component
 *
 * Step 3: Enter room dimensions via presets or custom values.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEstimator } from '../../../context/EstimatorContext';
import { ROOM_PRESETS } from '../../../data/presets';
import { trackPresetSelected } from '../../../utils/gtmTracking';
import { LEGACY_UI_ICONS } from '../../../data/productImages';
import { Room3DViewer } from '../../ui/room3d';
import { DimensionInput, UnitToggle } from '../../ui/DimensionInput';
import { useUnitMode } from '../../../hooks/useUnitMode';
import {
  Ruler,
  Move,
  ArrowLeftRight,
  ArrowUpDown,
  Square,
  SquareStack,
  Maximize,
  Grid3X3,
  LayoutTemplate,
  Pencil,
  AlertCircle,
  Info,
} from 'lucide-react';
import { MAX_WALL_HEIGHT } from '../../../calculations/constants';
import './Steps.css';

// Validation helper
interface ValidationError {
  field: string;
  message: string;
}

export const DimensionsStep: React.FC = () => {
  const { state, updateDimensionsConfig } = useEstimator();
  const { t } = useTranslation('wizard');
  const { dimensionsConfig, calculationMode, ceilingConfig, wallsConfig, applicationConfig } = state;
  const [showLightbox, setShowLightbox] = useState(false);
  // One ft/in <-> decimal toggle for the whole Length/Width/Height group.
  const [unitMode, setUnitMode] = useUnitMode('dimensions');

  // Validation logic
  const validationErrors = useMemo((): ValidationError[] => {
    const errors: ValidationError[] = [];
    if (dimensionsConfig.usePreset || !dimensionsConfig.customDimensions) {
      return errors;
    }

    const { length, width, height } = dimensionsConfig.customDimensions;

    if (length < 1) {
      errors.push({ field: 'length', message: t('dimensions.errors.length') });
    } else if (length > 200) {
      errors.push({ field: 'length', message: t('dimensions.errors.length') });
    }

    if (width < 1) {
      errors.push({ field: 'width', message: t('dimensions.errors.width') });
    } else if (width > 200) {
      errors.push({ field: 'width', message: t('dimensions.errors.width') });
    }

    if (calculationMode !== 'CeilingOnly') {
      if (height < 2) {
        errors.push({ field: 'height', message: t('dimensions.errors.height', { max: MAX_WALL_HEIGHT }) });
      } else if (height > MAX_WALL_HEIGHT) {
        errors.push({ field: 'height', message: t('dimensions.errors.height', { max: MAX_WALL_HEIGHT }) });
      }
    }

    return errors;
  }, [dimensionsConfig, calculationMode, t]);

  const getFieldError = (field: string): string | undefined => {
    return validationErrors.find(e => e.field === field)?.message;
  };

  // In CeilingOnly mode, we don't need height
  const isCeilingOnly = calculationMode === 'CeilingOnly';

  const handlePresetSelect = (presetId: string) => {
    updateDimensionsConfig({
      usePreset: true,
      presetId,
    });
    // Track preset selection — dimensions is step 4 in FullRoom mode
    const preset = ROOM_PRESETS.find(p => p.id === presetId);
    if (preset) {
      trackPresetSelected(calculationMode, preset.name, 'room_size', 4);
    }
  };

  const handleCustomToggle = () => {
    // If switching from a preset, seed custom dimensions from the selected preset
    const fallback = selectedPreset
      ? { length: selectedPreset.length, width: selectedPreset.width, height: selectedPreset.height }
      : { length: 20, width: 20, height: 10 };
    updateDimensionsConfig({
      usePreset: false,
      customDimensions: dimensionsConfig.customDimensions || fallback,
    });
  };

  const handleDimensionChange = (field: 'length' | 'width' | 'height', numValue: number) => {
    updateDimensionsConfig({
      customDimensions: {
        ...dimensionsConfig.customDimensions!,
        [field]: numValue,
      },
    });
  };

  const selectedPreset = ROOM_PRESETS.find(p => p.id === dimensionsConfig.presetId);

  // Get current wall height for slatwall conflict detection
  const currentWallHeight = useMemo(() => {
    if (!dimensionsConfig.usePreset && dimensionsConfig.customDimensions?.height) {
      return dimensionsConfig.customDimensions.height;
    }
    return selectedPreset?.height || 10;
  }, [dimensionsConfig, selectedPreset]);

  // Detect slatwall conflict
  const slatWallConflict = useMemo(() => {
    if (wallsConfig.slatWallNumPanels <= 0) return null;
    const slatWallTopHeight = wallsConfig.slatWallStartHeight + (wallsConfig.slatWallNumPanels * 0.5);
    if (slatWallTopHeight > currentWallHeight) {
      return {
        startHeight: wallsConfig.slatWallStartHeight,
        numPanels: wallsConfig.slatWallNumPanels,
        slatWallTop: slatWallTopHeight,
        wallHeight: currentWallHeight,
      };
    }
    return null;
  }, [wallsConfig, currentWallHeight]);

  return (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">{isCeilingOnly ? t('dimensions.titleCeiling') : t('dimensions.titleRoom')}</h2>
        <p className="step-description">
          {isCeilingOnly
            ? t('dimensions.descriptionCeiling')
            : t('dimensions.descriptionRoom')}
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="mode-toggle">
        <button
          type="button"
          className={`mode-button ${dimensionsConfig.usePreset ? 'active' : ''}`}
          onClick={() => updateDimensionsConfig({ usePreset: true })}
        >
          <LayoutTemplate size={16} />
          {t('shared.usePresets')}
        </button>
        <button
          type="button"
          className={`mode-button ${!dimensionsConfig.usePreset ? 'active' : ''}`}
          onClick={handleCustomToggle}
        >
          <Pencil size={16} />
          {t('shared.customDimensions')}
        </button>
      </div>

      {/* Presets Grid */}
      {dimensionsConfig.usePreset && (
        <div className="form-section">
          <h3 className="section-title">
            <Grid3X3 size={20} className="section-icon" />
            {t('dimensions.selectRoomSize')}
          </h3>
          <div className="presets-grid">
            {ROOM_PRESETS.map(preset => (
              <button
                key={preset.id}
                type="button"
                className={`preset-card ${dimensionsConfig.presetId === preset.id ? 'selected' : ''}`}
                onClick={() => handlePresetSelect(preset.id)}
              >
                <Maximize size={20} className="preset-icon" />
                <span className="preset-name">
                  {t('dimensions.presetSize', {
                    length: preset.length,
                    width: preset.width,
                    height: preset.height,
                  })}
                </span>
              </button>
            ))}
          </div>

          {/* Selected Preset Info with Diagram */}
          {selectedPreset && (
            <div className="preset-info-with-diagram-new">
              {/* Area Summary - Centered with divider */}
              <div className="d-flex flex-wrap justify-content-center align-items-center gap-4">
                <div className="area-item">
                  <Square size={18} />
                  <span className="area-label">
                    {isCeilingOnly || ceilingConfig.include ? t('dimensions.areaCeiling') : t('dimensions.areaFloor')}
                  </span>
                  <strong className="area-value">
                    {t('shared.squareFeet', { value: (selectedPreset.length * selectedPreset.width).toLocaleString() })}
                  </strong>
                </div>
                {!isCeilingOnly && (
                  <>
                    <div className="d-none d-sm-block" style={{ width: '1px', height: '24px', backgroundColor: '#dee2e6' }}></div>
                    <div className="area-item">
                      <SquareStack size={18} />
                      <span className="area-label">{t('dimensions.areaWall')}</span>
                      <strong className="area-value">
                        {t('shared.squareFeet', { value: (2 * (selectedPreset.length + selectedPreset.width) * selectedPreset.height).toLocaleString() })}
                      </strong>
                    </div>
                  </>
                )}
              </div>

              {/* 3D Viewer - Full Width */}
              <Room3DViewer
                length={selectedPreset.length}
                width={selectedPreset.width}
                height={selectedPreset.height}
                showHeight={!isCeilingOnly}
                highlightCeiling={isCeilingOnly}
                highlightWalls={!isCeilingOnly}
                wallColor={wallsConfig.color}
                ceilingColor={ceilingConfig.color}
                wallOrientation={wallsConfig.orientation}
                showCeiling={ceilingConfig.include}
                slatWallConfig={wallsConfig.slatWallNumPanels > 0 ? {
                  enabled: true,
                  startHeight: wallsConfig.slatWallStartHeight,
                  numPanelRows: wallsConfig.slatWallNumPanels,
                  color: wallsConfig.slatWallColor,
                } : null}
                garageConfig={applicationConfig?.garage?.enabled ? {
                  enabled: true,
                  width: applicationConfig.garage.width,
                  height: applicationConfig.garage.height,
                  numDoors: applicationConfig.garage.numDoors,
                  onWallType: applicationConfig.garage.onWallType !== 'None' ? applicationConfig.garage.onWallType : 'LengthWall',
                } : null}
              />
            </div>
          )}
        </div>
      )}

      {/* Custom Dimensions */}
      {!dimensionsConfig.usePreset && dimensionsConfig.customDimensions && (
        <div className="form-section">
          <h3 className="section-title">
            <Ruler size={20} className="section-icon" />
            {t('dimensions.enterDimensions')}
          </h3>
          <div className="row g-5 justify-content-center">
            <div className="col-auto">
              <div className={`d-flex flex-column align-items-center ${getFieldError('length') ? 'has-error' : ''}`}>
                <label htmlFor="length" className="field-label text-nowrap mb-2 d-inline-flex align-items-center gap-1">
                  <ArrowLeftRight size={16} />
                  {t('shared.fieldLength')}
                </label>
                <DimensionInput
                  id="length"
                  value={dimensionsConfig.customDimensions.length}
                  onChange={v => handleDimensionChange('length', v)}
                  mode={unitMode}
                  min={1}
                  max={200}
                  hasError={!!getFieldError('length')}
                  fieldId="custom-length"
                  ariaLabel={t('shared.lengthAria')}
                />
                {getFieldError('length') && (
                  <span className="field-error">
                    <AlertCircle size={14} />
                    {getFieldError('length')}
                  </span>
                )}
              </div>
            </div>

            <div className="col-auto d-none d-sm-flex align-items-center px-0">
              <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
            </div>

            <div className="col-auto">
              <div className={`d-flex flex-column align-items-center ${getFieldError('width') ? 'has-error' : ''}`}>
                <label htmlFor="width" className="field-label text-nowrap mb-2 d-inline-flex align-items-center gap-1">
                  <Move size={16} />
                  {t('shared.fieldWidth')}
                </label>
                <DimensionInput
                  id="width"
                  value={dimensionsConfig.customDimensions.width}
                  onChange={v => handleDimensionChange('width', v)}
                  mode={unitMode}
                  min={1}
                  max={200}
                  hasError={!!getFieldError('width')}
                  fieldId="custom-width"
                  ariaLabel={t('shared.fieldWidth')}
                />
                {getFieldError('width') && (
                  <span className="field-error">
                    <AlertCircle size={14} />
                    {getFieldError('width')}
                  </span>
                )}
              </div>
            </div>

            {!isCeilingOnly && (
              <>
                <div className="col-auto d-none d-sm-flex align-items-center px-0">
                  <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
                </div>

                <div className="col-auto">
                  <div className={`d-flex flex-column align-items-center ${getFieldError('height') ? 'has-error' : ''}`}>
                    <label htmlFor="height" className="field-label text-nowrap mb-2 d-inline-flex align-items-center gap-1">
                      <ArrowUpDown size={16} />
                      {t('dimensions.fieldHeight')}
                    </label>
                    <DimensionInput
                      id="height"
                      value={dimensionsConfig.customDimensions.height}
                      onChange={v => handleDimensionChange('height', v)}
                      mode={unitMode}
                      min={2}
                      max={MAX_WALL_HEIGHT}
                      hasError={!!getFieldError('height')}
                      fieldId="custom-height"
                      ariaLabel={t('dimensions.fieldHeight')}
                    />
                    {getFieldError('height') && (
                      <span className="field-error">
                        <AlertCircle size={14} />
                        {getFieldError('height')}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* One ft/in <-> decimal toggle for the whole dimension group */}
          <UnitToggle mode={unitMode} onChange={setUnitMode} />

          {/* Helper text about ceiling joists */}
          <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
            <div className="flex-shrink-0 text-info">
              <Info size={20} />
            </div>
            <div className="d-flex flex-column flex-md-row align-items-start gap-3">
              <p className="mb-0 small">
                {t('shared.joistNote')}
              </p>
              <div className="text-center flex-shrink-0">
                <img
                  src={LEGACY_UI_ICONS.dimensionsGuide}
                  alt={t('shared.joistImageAlt')}
                  style={{ maxWidth: '200px', cursor: 'pointer' }}
                  onClick={() => setShowLightbox(true)}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <span className="d-block text-muted mt-1" style={{ cursor: 'pointer', fontSize: '0.65rem' }} onClick={() => setShowLightbox(true)}>
                  {t('shared.clickFullSize')}
                </span>
              </div>
            </div>
          </div>

          {/* Lightbox with fade + zoom animation */}
          {showLightbox && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center lightbox-overlay"
              style={{ zIndex: 9999, cursor: 'pointer' }}
              onClick={() => setShowLightbox(false)}
            >
              <img
                src={LEGACY_UI_ICONS.dimensionsGuide}
                alt={t('shared.joistImageAlt')}
                className="lightbox-image"
              />
            </div>
          )}

          {/* Calculated Area and Diagram */}
          <div className="dimensions-summary-with-diagram-new">
            {/* Area Summary - Centered with divider */}
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-4">
              <div className="area-item">
                <Square size={18} />
                <span className="area-label">
                  {isCeilingOnly || ceilingConfig.include ? t('dimensions.areaCeiling') : t('dimensions.areaFloor')}
                </span>
                <strong className="area-value">
                  {t('shared.squareFeet', {
                    value: (
                      dimensionsConfig.customDimensions.length *
                      dimensionsConfig.customDimensions.width
                    ).toFixed(0),
                  })}
                </strong>
              </div>
              {!isCeilingOnly && (
                <>
                  <div className="d-none d-sm-block" style={{ width: '1px', height: '24px', backgroundColor: '#dee2e6' }}></div>
                  <div className="area-item">
                    <SquareStack size={18} />
                    <span className="area-label">{t('dimensions.areaWall')}</span>
                    <strong className="area-value">
                      {t('shared.squareFeet', {
                        value: (
                          2 *
                          (dimensionsConfig.customDimensions.length +
                            dimensionsConfig.customDimensions.width) *
                          dimensionsConfig.customDimensions.height
                        ).toFixed(0),
                      })}
                    </strong>
                  </div>
                </>
              )}
            </div>

            {/* 3D Viewer - Full Width */}
            <Room3DViewer
              length={dimensionsConfig.customDimensions.length}
              width={dimensionsConfig.customDimensions.width}
              height={dimensionsConfig.customDimensions.height}
              showHeight={!isCeilingOnly}
              highlightCeiling={isCeilingOnly}
              highlightWalls={!isCeilingOnly}
              wallColor={wallsConfig.color}
              ceilingColor={ceilingConfig.color}
              wallOrientation={wallsConfig.orientation}
              showCeiling={ceilingConfig.include}
              slatWallConfig={wallsConfig.slatWallNumPanels > 0 ? {
                enabled: true,
                startHeight: wallsConfig.slatWallStartHeight,
                numPanelRows: wallsConfig.slatWallNumPanels,
                color: wallsConfig.slatWallColor,
              } : null}
              garageConfig={applicationConfig?.garage?.enabled ? {
                enabled: true,
                width: applicationConfig.garage.width,
                height: applicationConfig.garage.height,
                numDoors: applicationConfig.garage.numDoors,
                onWallType: applicationConfig.garage.onWallType !== 'None' ? applicationConfig.garage.onWallType : 'LengthWall',
              } : null}
            />
          </div>
        </div>
      )}
      {/* SlatWall recalculation notice */}
      {wallsConfig.slatWallNumPanels > 0 && !slatWallConflict && (
        <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-3" role="alert">
          <div className="flex-shrink-0 text-info">
            <Info size={20} />
          </div>
          <div>
            <strong>{t('dimensions.slatWall.title')}</strong>
            <p className="mb-0 small mt-1">
              {t('dimensions.slatWall.summary', {
                startHeight: wallsConfig.slatWallStartHeight,
                panels: wallsConfig.slatWallNumPanels,
                from: wallsConfig.slatWallStartHeight,
                to: (wallsConfig.slatWallStartHeight + wallsConfig.slatWallNumPanels * 0.5).toFixed(1),
              })}
              {wallsConfig.slatWallCoveragePercent ? ' ' + t('dimensions.slatWall.coverageNote', {
                percent: wallsConfig.slatWallCoveragePercent,
                wallHeight: currentWallHeight,
              }) : ''}
            </p>
          </div>
        </div>
      )}

      {/* SlatWall conflict warning */}
      {slatWallConflict && (
        <div className="validation-warning" style={{ marginTop: '16px' }}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {t('dimensions.slatWall.conflict', {
            wallHeight: slatWallConflict.wallHeight,
            slatWallTop: slatWallConflict.slatWallTop.toFixed(1),
          })}
        </div>
      )}
    </div>
  );
};

export default DimensionsStep;
